// o4AnchorHelper.js
//
// This module provides AI‑driven anchor generation for the o4‑mini model.
// It mirrors the functionality of anchorHelper.js but is named to
// clearly indicate that it works with the o4 configuration.  The
// heuristics remain removed so that all anchor inference is deferred
// to the AI back end.  When the back end is unavailable or fails,
// the only anchor guaranteed is the employer name from the work
// history entry.

import axios from 'axios';
import { AI_FUNCTIONS_BASE_URL } from '../config/api';

/**
 * Generate a minimal set of anchors from a work history entry.  This
 * synchronous helper returns only the employer name when provided.
 * Use inferAnchorsWithAI for AI‑driven anchoring.
 *
 * @param {Object} entry – A single work history entry containing at
 *   least jobTitle and employer properties.
 * @returns {Object} An anchors object with employerNames, environment,
 *   staff, and location arrays.
 */
export function generateAnchors(entry = {}) {
  const anchors = {
    employerNames: [],
    environment: [],
    staff: [],
    location: [],
  };
  const employer = entry?.employer?.trim();
  if (employer) {
    anchors.employerNames.push(employer);
  }
  return anchors;
}

/**
 * Infer anchors using the back‑end AI service.  This function posts
 * to the Azure Function endpoint using the ``generateAnchors`` prompt
 * and expects a response containing an ``anchors`` object.  If the
 * request fails or the expected data is absent, a minimal anchors
 * object (containing only the employer name) is returned instead.
 *
 * @param {Object} entry – A work history entry containing
 *   jobTitle, employer, and optionally detailed responsibilities.
 * @param {string} userId – The identifier for the current user.  This
 *   value is forwarded to the back‑end for token tracking.
 * @returns {Promise<Object>} A promise resolving to an anchors object.
 */
export async function inferAnchorsWithAI(entry = {}, userId = '') {
  try {
    const payload = {
      prompt: 'generateAnchors',
      workHistory: entry,
      userID: userId,
    };
    const response = await axios.post(
      `${AI_FUNCTIONS_BASE_URL}/api/jobSeriesSearchHttp`,
      payload,
      { headers: { 'Content-Type': 'application/json' } },
    );
    if (response && response.data && response.data.anchors) {
      return response.data.anchors;
    }
  } catch (err) {
    // Fall through to fallback below when the AI call fails
    console.error('inferAnchorsWithAI failed, falling back to minimal anchors:', err);
  }
  // Fallback: return a minimal anchors object when AI inference is unavailable.
  const fallbackAnchors = {
    employerNames: [],
    environment: [],
    staff: [],
    location: [],
  };
  const employerName = entry?.employer?.trim();
  if (employerName) {
    fallbackAnchors.employerNames.push(employerName);
  }
  return fallbackAnchors;
}
